import React, { useEffect, useRef, useState } from 'react';
import { AUTOMATED_PAYMENTS_PURPOSE_OPTIONS } from '@/utils/constants/automations';
import { Popover } from '@mui/material';
import clsx from 'clsx';

import { ChevronToggle } from '@/components/common-components/ChevronToggle';
import InputComponent from '@/components/common-components/InputComponent/InputComponent';
import ModalCommonButtonsContainer from '@/components/common-components/ModalComponent/ModalCommonButtonsContainer/ModalCommonButtonsContainer';
import RequiredAsterisk from '@/components/common-components/RequiredAsterisk/RequiredAsterisk';
import ShortString from '@/components/common-components/ShortString/ShortString';
import TickIcon2 from '@/components/Icons/icons/TickIcon2';

interface Props {
  selectedPurpose: string | null;
  setSelectedPurpose: React.Dispatch<React.SetStateAction<string | null>>;
}

const PurposeInput: React.FC<Props> = ({ selectedPurpose, setSelectedPurpose }) => {
  const [purposeDropdown, setPurposeDropdown] = useState<HTMLDivElement | null>(null);
  const [tempCustomPurpose, setTempCustomPurpose] = useState<string | null>(null);
  const [isCustomPurpose, setIsCustomPurpose] = useState(false);

  const customPurposeSelectRef = useRef<HTMLDivElement | null>(null);
  const customPurposeSubmitButtonRef = useRef<HTMLDivElement | null>(null);

  const checkIfCustomPurpose = (purposeToCheck: string | null) =>
    !AUTOMATED_PAYMENTS_PURPOSE_OPTIONS.some((purpose) => purpose.value === purposeToCheck);

  const getSelectedValue = () => {
    let result: string;

    const isCustom = checkIfCustomPurpose(selectedPurpose);

    if (isCustom && selectedPurpose) {
      result = selectedPurpose;
    } else {
      result = AUTOMATED_PAYMENTS_PURPOSE_OPTIONS.find((purpose) => purpose.value === selectedPurpose)?.label || '';
    }

    return result;
  };

  useEffect(() => {
    if (selectedPurpose) {
      const checkIfPurposeExistsInOptions = AUTOMATED_PAYMENTS_PURPOSE_OPTIONS.find(
        (purpose) => purpose.value === selectedPurpose
      );
      if (checkIfPurposeExistsInOptions) {
        setIsCustomPurpose(false);
      } else {
        setTempCustomPurpose(selectedPurpose);
      }
    }
  }, [selectedPurpose]);

  return (
    <div>
      <label className="text-sm text-subdued mb-1 flex flex-start gap-1">
        Purpose <RequiredAsterisk />
      </label>
      <div
        ref={customPurposeSelectRef}
        onClick={(e) => setPurposeDropdown(e.currentTarget)}
        className={clsx(
          'border-default rounded border-hover flex flex-between items-center p-2 rounded cursor-pointer',
          {
            'border-highlight': purposeDropdown,
          }
        )}>
        <span className="flex flex-start gap-2">
          {selectedPurpose ? (
            <ShortString name={getSelectedValue()} stringLength={35} placement="bottom" style={{ fontSize: '16px' }} />
          ) : (
            <span className="text-base text-disabled">Select a purpose</span>
          )}
        </span>
        <ChevronToggle toggle={Boolean(purposeDropdown)} />
      </div>
      <Popover
        anchorEl={purposeDropdown}
        open={Boolean(purposeDropdown)}
        onClose={() => setPurposeDropdown(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: customPurposeSelectRef.current?.clientWidth,
            zIndex: 99999,
            overflow: 'auto',
            maxHeight: '300px',
            mt: 1,
            padding: '8px',
            '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
          },
        }}>
        {AUTOMATED_PAYMENTS_PURPOSE_OPTIONS?.map((purpose, index) => {
          const isSelected = purpose.value === selectedPurpose;
          return (
            <div
              key={index}
              className={clsx('p-2 cursor-pointer hover-bg flex flex-between items-center list-item-margin-8', {
                'text-highlight  bg-selected': isSelected,
              })}
              onClick={() => {
                setSelectedPurpose(purpose.value);
                setPurposeDropdown(null);
                setTempCustomPurpose(null);
              }}>
              <ShortString name={purpose.label} stringLength={30} />
              {isSelected && <TickIcon2 width={14} height={14} color="var(--icon-highlight)" />}
            </div>
          );
        })}
        <div className="custom-sendername-div">
          <InputComponent
            type="text"
            inputStyle={{
              color:
                selectedPurpose?.toLowerCase() === tempCustomPurpose?.toLowerCase()
                  ? 'var(--text-highlight)'
                  : 'var(--text-default)',
            }}
            inputWrapperClass={clsx({
              'border-hover': !isCustomPurpose,
            })}
            value={tempCustomPurpose || ''}
            onChange={(e) => setTempCustomPurpose(e.target.value.toLowerCase())}
            placeholder="Other"
            onFocus={() => {
              setIsCustomPurpose(true);

              setTimeout(() => {
                if (customPurposeSubmitButtonRef.current) {
                  customPurposeSubmitButtonRef.current?.scrollIntoView({
                    behavior: 'smooth',
                  });
                }
              }, 500);
            }}
            right={
              selectedPurpose &&
              tempCustomPurpose &&
              selectedPurpose?.toLowerCase() === tempCustomPurpose?.toLowerCase() ? (
                <TickIcon2 width={14} height={14} color="var(--icon-highlight)" />
              ) : (
                ''
              )
            }
          />
          {isCustomPurpose && (
            <div className="mt-2" ref={customPurposeSubmitButtonRef}>
              <ModalCommonButtonsContainer
                onClose={() => {
                  setIsCustomPurpose(false);
                  setTempCustomPurpose('');
                }}
                onSave={() => {
                  setIsCustomPurpose(false);
                  setSelectedPurpose(tempCustomPurpose);
                  setPurposeDropdown(null);
                }}
                cancelButtonText="Cancel"
                submitButtonText="Save"
                disabled={!tempCustomPurpose}
              />
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default PurposeInput;
